import React from "react";
import {
  TableRow,
  TableCell,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { IChangeReview } from "../../../../types/review";
import { processCarWashReview } from "../../../../services/carWash.service";
import ReviewDetailsModal from "../../CarWash/screens/components/ReviewDetailsModal";

type Props = {
  item: IChangeReview;
  onActionComplete: () => void;
  onError: (message: string) => void;
};

type CountdownInfo = {
  label: string;
  color: "success" | "warning" | "error" | "default";
};

// Phase 28 countdown (D-04): deadline is createdAt + 72h. Color thresholds:
// green >= 12h remaining, orange 4-12h, red < 4h or expired.
function computeCountdown(
  t: (key: string, opts?: Record<string, unknown>) => string,
  item: IChangeReview,
  nowMs: number
): CountdownInfo {
  if (!item.createdAt) {
    return { label: "—", color: "default" };
  }
  const base = new Date(item.createdAt).getTime() + 72 * 3600 * 1000;
  if (Number.isNaN(base)) {
    return { label: "—", color: "default" };
  }

  const remainingMs = base - nowMs;
  if (remainingMs <= 0) {
    return { label: t("manageCarWashes.expired"), color: "error" };
  }

  const totalMinutes = Math.floor(remainingMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  let color: CountdownInfo["color"];
  if (h >= 12) {
    color = "success";
  } else if (h >= 4) {
    color = "warning";
  } else {
    color = "error";
  }

  return { label: t("manageCarWashes.countdownRemaining", { hours: h, minutes: m }), color };
}

// The populated owner/carWash may be an object (populated) or a string (id).
function ownerLabel(owner: IChangeReview["owner"]): string {
  if (!owner || typeof owner === "string") return "—";
  const name = `${owner.firstName ?? ""} ${owner.lastName ?? ""}`.trim();
  return name || "—";
}

function ownerPhone(owner: IChangeReview["owner"]): string {
  if (!owner || typeof owner === "string") return "";
  if (!owner.phone) return "";
  return `${owner.country_code ?? ""} ${owner.phone}`.trim();
}

// carWash is legitimately null for create-type reviews (requestType "carWash"):
// per Phase 34-01 the car wash record is only created once the RM approves, so
// the proposed name lives in changes.legalName until then — fall back to it
// before showing the em-dash placeholder.
function carWashLabel(
  carWash: IChangeReview["carWash"],
  changes?: IChangeReview["changes"]
): string {
  if (carWash && typeof carWash !== "string" && carWash.legalName) {
    return carWash.legalName;
  }
  const proposedName = changes?.legalName;
  if (typeof proposedName === "string" && proposedName) return proposedName;
  return "—";
}

const RMPendingReviewRow = ({ item, onActionComplete, onError }: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  // Tick once a minute so the countdown stays roughly current without churn.
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const countdown = computeCountdown(t, item, nowMs);

  // Stop row-click when interacting with action buttons.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Acts on item._id (the review id) only, via processCarWashReview — no
  // carWashId dependency. carWash is legitimately null for brand-new
  // car-wash creation requests (creation is deferred to approval time), so
  // deriving/guarding on a carWashId here would block approving those.
  const runAction = async (
    status: "approved" | "rejected",
    comment?: string
  ) => {
    if (!item._id) {
      onError(t("manageCarWashes.missingReviewReference"));
      return;
    }
    setBusy(true);
    try {
      await processCarWashReview(item._id, status, comment);
      onActionComplete();
    } catch (err: any) {
      onError(
        err?.message ||
          (status === "approved"
            ? t("serviceErrors.acceptChangeRequest")
            : t("serviceErrors.rejectChangeRequest"))
      );
      // Reconcile on failure too. Refreshing only on success left a failed
      // action showing stale rows until a manual page reload — the symptom that
      // made the 2026-09-23 approve bug so hard to read from the UI.
      onActionComplete();
    } finally {
      setBusy(false);
    }
  };

  const phone = ownerPhone(item.owner);

  return (
    <>
      <TableRow hover tabIndex={-1}>
        <TableCell align="left">{carWashLabel(item.carWash, item.changes)}</TableCell>
        <TableCell align="left">
          {ownerLabel(item.owner)}
          {phone ? ` (${phone})` : ""}
        </TableCell>
        <TableCell align="left">
          <Chip
            data-testid="countdown-chip"
            label={countdown.label}
            color={countdown.color}
            size="small"
          />
        </TableCell>
        <TableCell align="left" onClick={stop}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={t("manageCarWashes.viewChanges")}>
              <IconButton size="small" onClick={() => setModalOpen(true)}>
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={busy}
              onClick={() => runAction("approved")}
            >
              {t("common.approve")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busy}
              onClick={() => runAction("rejected")}
            >
              {t("common.reject")}
            </Button>
          </Stack>
        </TableCell>
      </TableRow>

      {modalOpen && (
        <ReviewDetailsModal
          open={modalOpen}
          review={item}
          onClose={() => setModalOpen(false)}
          onApprove={() => runAction("approved")}
          onReject={(_id, comment) => runAction("rejected", comment)}
          actionLoading={busy}
        />
      )}
    </>
  );
};

export default RMPendingReviewRow;
